import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, authStore } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Icon } from '../components/icons';
import { Button, Card, Spinner } from '../components/ui';

/**
 * The landing page for an invitation link.
 *
 * It has to work for someone who is signed out, signed in as the right person, or signed
 * in as the wrong one — the link arrives in a mail client, and which account the browser
 * happens to hold is not something the sender can control. Each of those gets a different
 * next step rather than one generic refusal.
 */
export function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const { auth } = useAuth();

  const [error, setError] = useState<string | null>(null);

  const preview = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => api.invitations.preview(token),
    enabled: token.length > 0,
    retry: false,
  });

  const accept = useMutation({
    mutationFn: () => api.invitations.accept(token),
    onSuccess: async (joined) => {
      // The current token is scoped to whichever workspace they were already in, so it
      // does not yet carry the new membership. Switching to the workspace named in the
      // accept response is what makes the invitation actually take effect.
      try {
        const result = await api.account.switchTenant(joined.tenantId);
        authStore.write({
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
          expiresUtc: result.expiresUtc,
          user: result.user,
          tenant: result.tenant,
        });
      } catch {
        // Switching is a convenience: they are a member either way, and the account page
        // can switch them. Failing here must not look like the invitation failed.
      }
      window.location.assign('/');
    },
    onError: (e: Error) => setError(e.message),
  });

  useEffect(() => {
    if (!token) navigate('/', { replace: true });
  }, [token, navigate]);

  if (!token) return null;

  return (
    <div className="mx-auto flex min-h-full max-w-md flex-col justify-center p-6">
      <Card className="p-6">
        <div className="mb-4 flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-lg
                           bg-[var(--ff-primary)] text-[var(--ff-on-primary)]">
            <Icon name="sparkle" size={17} />
          </span>
          <span className="font-semibold">FormForge</span>
        </div>

        {preview.isLoading && <Spinner label="Checking the invitation" />}

        {preview.isError && (
          <>
            <h1 className="mb-2 text-lg font-semibold">This invitation is not valid</h1>
            <p className="text-sm text-[var(--ff-text-muted)]">
              {(preview.error as Error).message}
            </p>
            <Button className="mt-4" onClick={() => navigate('/')}>Go to FormForge</Button>
          </>
        )}

        {preview.data && (
          <>
            <h1 className="mb-2 text-lg font-semibold">
              Join {preview.data.workspaceName}
            </h1>
            <p className="mb-4 text-sm text-[var(--ff-text-muted)]">
              {preview.data.invitedBy
                ? `${preview.data.invitedBy} invited `
                : 'You have been invited as '}
              <strong>{preview.data.email}</strong>
              {preview.data.invitedBy && ` as ${preview.data.role}`}.
            </p>

            {error && (
              <p className="mb-3 rounded-md bg-[var(--ff-danger-soft)] p-2 text-sm
                            text-[var(--ff-danger)]">
                {error}
              </p>
            )}

            {/* Signed out: the invitation is bound to an address, so they need an account
                with it before anything can be accepted. */}
            {!auth && (
              <div className="space-y-2">
                <p className="text-sm">
                  Sign in as {preview.data.email} to accept, or create an account with that
                  address.
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    onClick={() => navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`)}
                  >
                    Sign in
                  </Button>
                  <Button
                    onClick={() => navigate(`/register?next=${encodeURIComponent(location.pathname + location.search)}`)}
                  >
                    Create an account
                  </Button>
                </div>
              </div>
            )}

            {/* Signed in as somebody else. Named explicitly, because "two accounts" is the
                usual cause and a generic refusal leaves people stuck. */}
            {auth && auth.user.email !== preview.data.email && (
              <div className="space-y-2">
                <p className="text-sm">
                  You are signed in as <strong>{auth.user.email}</strong>, but this
                  invitation is for <strong>{preview.data.email}</strong>.
                </p>
                <Button
                  onClick={() => {
                    authStore.clear();
                    navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`);
                  }}
                >
                  Sign in as {preview.data.email}
                </Button>
              </div>
            )}

            {auth && auth.user.email === preview.data.email && (
              <Button
                variant="primary"
                onClick={() => accept.mutate()}
                disabled={accept.isPending}
              >
                {accept.isPending ? 'Joining…' : `Join ${preview.data.workspaceName}`}
              </Button>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
